import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useAlbumSelectionStore from '../../../stores/useAlbumSelectionStore';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';
import { COMING_SOON_ALBUM_TYPES } from '../../../lib/const';
import CustomTooltip from '../../common/Tooltip';
import { ReactComponent as HelpIcon } from '../../../images/icons/ProductSelectionPage/helpicon.svg';

const AlbumFormInputs = () => {
  const {
    albumForm,
    processAlbumTypesData,
    processAlbumPapersData,
    processAlbumSizesData,
    processFabricsData,
    processStampingsData,
    processPreDesignedCoversData,
    processFontOptionsData,
    setAlbumPaperLoading,
    setAlbumSizeLoading,
    setFabricsLoading,
    setStampingsLoading,
    setPreDesignedCoverLoading,
    setFontOptionsLoading,
    initializeAlbumForm,
    updateAlbumInput,
    updateAlbumTextInput,
    getAlbumInputKeys,
    resetAlbumForm,
    albumItems,
    setIsAlbumEditInitializing,
  } = useAlbumSelectionStore();

  const { setModalIsOpen } = useProductSelectionStore();
  const selectRef = useRef(null);

  const albumTypeSelectedValue = albumForm?.inputs?.albumType?.selectedValue;
  const isComingSoonAlbumType = COMING_SOON_ALBUM_TYPES.includes(albumTypeSelectedValue);
  const albumPaperSelectedValue = albumForm?.inputs?.albumPaper?.selectedValue;
  const albumSizeSelectedValue = albumForm?.inputs?.albumSize?.selectedValue;
  const coverFabricSelectedValue = albumForm?.inputs?.coverFabric?.selectedValue;
  const interiorCoverFabricSelectedValue = albumForm?.inputs?.interiorFabric?.selectedValue;

  // --- Queries ---
  const albumTypesQuery = useQuery({
    queryKey: ['albumTypes'],
    queryFn: () => httpClient.get('albums/types'),
    staleTime: Infinity,
    onSuccess: (response) => {
      processAlbumTypesData(response);
    },
  });

  useEffect(() => {
    if (albumTypesQuery.data && !albumTypesQuery.isLoading) {
      processAlbumTypesData(albumTypesQuery.data);
    }
  }, [albumTypesQuery.data, albumTypesQuery.isLoading, processAlbumTypesData]);

  // Build the album-type-specific fields before cached dependent queries are
  // applied. On client-side navigation those queries may all resolve immediately.
  useEffect(() => {
    if (!albumTypeSelectedValue || albumTypeSelectedValue === 'default') return;

    const albumTypeText = albumForm?.inputs?.albumType?.selectedValueText;
    if (albumTypeText) {
      initializeAlbumForm(albumTypeSelectedValue, albumTypeText);
    }
  }, [albumTypeSelectedValue, initializeAlbumForm]);

  const albumPapersQuery = useQuery({
    queryKey: ['albumPapers', albumTypeSelectedValue],
    queryFn: () => httpClient.get(`albums/album_papers?album_type_id=${albumTypeSelectedValue}`),
    enabled: !!albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && !isComingSoonAlbumType,
    staleTime: Infinity,
    onSuccess: (response) => {
      processAlbumPapersData(response);
    },
  });

  useEffect(() => {
    if (albumPapersQuery.data && !albumPapersQuery.isLoading) {
      processAlbumPapersData(albumPapersQuery.data);
    }
  }, [albumPapersQuery.data, albumPapersQuery.isLoading, processAlbumPapersData]);

  useEffect(() => {
    if (albumTypeSelectedValue && albumTypeSelectedValue !== 'default') {
      setAlbumPaperLoading(albumPapersQuery.isLoading);
    }
  }, [albumTypeSelectedValue, albumPapersQuery.isLoading, setAlbumPaperLoading]);

  const albumSizesQuery = useQuery({
    queryKey: ['albumSizes', albumPaperSelectedValue],
    queryFn: () => httpClient.get(`albums/sizes?album_paper_id=${albumPaperSelectedValue}`),
    enabled: !!albumPaperSelectedValue && albumPaperSelectedValue !== 'default',
    staleTime: Infinity,
    onSuccess: (response) => {
      processAlbumSizesData(response);
    },
  });

  useEffect(() => {
    if (albumSizesQuery.data && !albumSizesQuery.isLoading) {
      processAlbumSizesData(albumSizesQuery.data);
    }
  }, [albumSizesQuery.data, albumSizesQuery.isLoading, processAlbumSizesData]);

  useEffect(() => {
    if (albumPaperSelectedValue && albumPaperSelectedValue !== 'default' && albumSizesQuery.isLoading) {
      setAlbumSizeLoading(true);
    }
  }, [albumPaperSelectedValue, albumSizesQuery.isLoading, setAlbumSizeLoading]);

  const fabricsQuery = useQuery({
    queryKey: ['fabrics', albumSizeSelectedValue],
    queryFn: () => httpClient.get('albums/fabrics'),
    enabled: !!albumSizeSelectedValue && albumSizeSelectedValue !== 'default',
    staleTime: Infinity,
    onSuccess: (response) => {
      processFabricsData(response);
    },
  });

  useEffect(() => {
    if (fabricsQuery.data && !fabricsQuery.isLoading) {
      processFabricsData(fabricsQuery.data);
    }
  }, [fabricsQuery.data, fabricsQuery.isLoading, processFabricsData]);

  useEffect(() => {
    if (albumSizeSelectedValue && albumSizeSelectedValue !== 'default' && fabricsQuery.isLoading) {
      setFabricsLoading(true);
    }
  }, [albumSizeSelectedValue, fabricsQuery.isLoading, setFabricsLoading]);

  const stampingsQuery = useQuery({
    queryKey: ['stampings', coverFabricSelectedValue, interiorCoverFabricSelectedValue],
    queryFn: () => httpClient.get('albums/stampings'),
    enabled: ((!!interiorCoverFabricSelectedValue && interiorCoverFabricSelectedValue !== 'default')) && !!coverFabricSelectedValue && coverFabricSelectedValue !== 'default',
    staleTime: Infinity,
    onSuccess: (response) => {
      processStampingsData(response);
    },
  });

  useEffect(() => {
    if (stampingsQuery.data && !stampingsQuery.isLoading) {
      processStampingsData(stampingsQuery.data);
    }
  }, [stampingsQuery.data, stampingsQuery.isLoading, processStampingsData]);

  useEffect(() => {
    if (coverFabricSelectedValue && coverFabricSelectedValue !== 'default' && stampingsQuery.isLoading) {
      setStampingsLoading(true);
    }
  }, [coverFabricSelectedValue, stampingsQuery.isLoading, setStampingsLoading]);

  const preDesignedCoversQuery = useQuery({
    queryKey: ['preDesignedCovers'],
    queryFn: () => httpClient.get('albums/pre_designed_covers'),
    enabled: !!albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && !isComingSoonAlbumType,
    staleTime: Infinity,
    onSuccess: (response) => {
      processPreDesignedCoversData(response);
    },
  });

  useEffect(() => {
    if (preDesignedCoversQuery.data && !preDesignedCoversQuery.isLoading) {
      processPreDesignedCoversData(preDesignedCoversQuery.data);
    }
  }, [
    preDesignedCoversQuery.data,
    preDesignedCoversQuery.isLoading,
    processPreDesignedCoversData,
  ]);

  useEffect(() => {
    if (albumTypeSelectedValue && albumTypeSelectedValue !== 'default') {
      setPreDesignedCoverLoading(preDesignedCoversQuery.isLoading);
    }
  }, [albumTypeSelectedValue, preDesignedCoversQuery.isLoading, setPreDesignedCoverLoading]);

  const fontOptionsQuery = useQuery({
    queryKey: ['fontOptions'],
    queryFn: () => httpClient.get('albums/font_options'),
    enabled: !!albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && !isComingSoonAlbumType,
    staleTime: Infinity,
    onSuccess: (response) => {
      processFontOptionsData(response);
    },
  });

  useEffect(() => {
    if (fontOptionsQuery.data && !fontOptionsQuery.isLoading) {
      processFontOptionsData(fontOptionsQuery.data);
    }
  }, [fontOptionsQuery.data, fontOptionsQuery.isLoading, processFontOptionsData]);

  useEffect(() => {
    if (albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && fontOptionsQuery.isLoading) {
      setFontOptionsLoading(true);
    }
  }, [albumTypeSelectedValue, fontOptionsQuery.isLoading, setFontOptionsLoading]);

  // Handlers
  const handleTextInputChange = (key, value) => {
    setIsAlbumEditInitializing(false);
    updateAlbumTextInput(key, value);
  };

  const handleInputChange = (key, e) => {
    setIsAlbumEditInitializing(false);
    const input = albumForm?.inputs?.[key];

    if (key === 'albumType') {
      if (e.target.value === 'default') {
        resetAlbumForm();
        return;
      }
      const selectedText = e.target.options[e.target.selectedIndex].text;
      initializeAlbumForm(e.target.value, selectedText);
      return;
    }

    const albumInputKeys = getAlbumInputKeys();
    if (albumInputKeys.includes(key)) {
      const selectedOption = input?.options?.find(
        (option) => option?.value?.toString() === e.target.value,
      );
      const additionalData = {
        price: selectedOption?.price,
        perSpreadPrice: selectedOption?.perSpreadPrice,
        baseSpreads: selectedOption?.baseSpreads,
      };
      const selectedText = e.target.options[e.target.selectedIndex].text;

      if (key === 'numberOfSpreads') {
        const newSpreads = parseInt(e.target.value, 10);
        const filesCount = albumItems.length;
        if (newSpreads > filesCount || (filesCount > 10 && newSpreads !== filesCount)) {
          setModalIsOpen(true);
        }
      }

      updateAlbumInput(key, e.target.value, selectedText, additionalData);
    }
  };

  if (!albumForm || !albumForm.inputs) return null;

  return (
    <div className={styles.printsFormStack} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: '15px' }}>
      {Object.keys(albumForm.inputs)
        .filter((key) => {
          if (isComingSoonAlbumType) {
            return key === 'albumType';
          }
          if (albumForm.inputs[key]?.isHidden) {
            return false;
          }
          return true;
        })
        .map((key) => {
          const input = albumForm.inputs[key];
          if (!input) return null;

          const inputType = input.inputType || 'select';

          return (
            <div key={key} style={input.style}>
              <div className="input-container paper-type">
                <div className="label-container">
                  <div className="div-block-150" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {input.labelHtml ? (
                      <div
                        className="sub-label-form"
                        dangerouslySetInnerHTML={{ __html: input.labelHtml }}
                      />
                    ) : (
                      <div className="sub-label-form">{input.label}</div>
                    )}
                    {input.tooltip && (
                      <CustomTooltip
                        title={`${input.tooltip.text} <a href="${input.tooltip.url}" target="_blank" rel="noopener noreferrer" style="color:#0000FF;text-decoration:underline;">${input.tooltip.url}</a>`}
                      >
                        <HelpIcon
                          style={{ fill:"#989b9fff", width: '13px', height: '13px', flexShrink: 0, cursor: 'pointer' }}
                        />
                      </CustomTooltip>
                    )}
                  </div>
                </div>
                <div className="form-block-input w-form">
                  {inputType === 'text' ? (
                      <textarea  
                        type="text"
                        className="text-field-2 w-input"
                        maxLength="256"
                        name={`field-${key}`}
                        placeholder={input.placeholder || ''}
                        value={input.selectedValue || ''}
                        onChange={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = `${e.target.scrollHeight}px`;
                          handleTextInputChange(key, e.target.value);
                        }}
                        disabled={input.isDisabled}
                        rows={1}
                        style={{ resize: 'none', overflow: 'hidden', minHeight: '38px' }}
                      />
                  ) : (
                    <form className="form-input select-field">
                      <select
                        ref={selectRef}
                        className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                        style={{ width: '100%' }}
                        onChange={(e) => handleInputChange(key, e)}
                        value={input.selectedValue || 'default'}
                        disabled={key === 'clamshellUpgrade' && !!input.isDisabled}
                        title={
                          key === 'clamshellUpgrade' && input.isDisabled && input.disabledMessage
                            ? input.disabledMessage
                            : undefined
                        }
                      >
                        {input.isLoading === true && (!input.options || input.options.length === 0) ? (
                          <option value="">Loading...</option>
                        ) : (
                          <option value="default">Choose One</option>
                        )}
                        {input.options && input.options.length > 0 && input.options
                          .filter((option) => !option.text?.endsWith('**'))
                          .map((option) => {
                            if (!option || !option.value || !option.text) return null;
                            const isSizeDropdown = key === 'albumSize' || key === 'debossingType' || key === 'clamshellDebossingType' || key === 'clamshellUpgrade';
                            const displayText = isSizeDropdown
                              ? option.text
                              : option.text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();
                            return (
                              <option key={option.value} value={option.value}>
                                {displayText}
                              </option>
                            );
                          })
                          .filter(Boolean)
                        }
                      </select>
                      <img
                        src={ChevronIcon}
                        alt=""
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', width: '16px', height: '16px' }}
                      />
                    </form>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default AlbumFormInputs;
